import React, { useState } from 'react';
import { Network, Calculator, BookOpen, TrendingUp, Brain, Lightbulb, Play, Eye, Download, Star, Hash, Square, Target, Triangle, BarChart3, Compass, Move } from 'lucide-react';
import { allSamples } from '../data/knowledgeGraph';

const KnowledgeGraphSamples = ({ onSampleSelect }) => {
  const [selectedSample, setSelectedSample] = useState(null);
  const [selectedDifficulties, setSelectedDifficulties] = useState({
    quadratic: 'fundamental',
    circle_geometry: 'fundamental',
    factor_remainder: 'fundamental',
    probability: 'fundamental',
    statistics: 'fundamental',
    trigonometry: 'fundamental',
    number_system: 'fundamental',
    identities: 'fundamental',
    simultaneous_equations: 'fundamental',
    geometry: 'fundamental',
    locus: 'fundamental',
    percentages: 'fundamental',
    transformation_symmetry: 'fundamental',
    exponential_logarithm: 'fundamental',
    inequalities_linear_programming: 'fundamental',
    rates_ratios_variations: 'fundamental',
    arithmetic_geometric_sequences: 'fundamental',
    mensuration: 'fundamental',
    trigonometry_2d_3d: 'fundamental',
    straight_line_equations: 'fundamental'
  });
  const [viewMode, setViewMode] = useState('preview'); // 'preview', 'full'

  // Simple LaTeX renderer for problem text
  const renderLatexText = (text) => {
    if (!text) return '';
    
    // Simple LaTeX to HTML conversion for basic expressions
    let processedText = text;
    
    // Convert LaTeX fractions
    processedText = processedText.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '<span class="frac"><span class="num">$1</span><span class="denom">$2</span></span>');
    
    // Convert LaTeX superscripts
    processedText = processedText.replace(/\^(\d+)/g, '<sup>$1</sup>');
    processedText = processedText.replace(/\^\{([^}]+)\}/g, '<sup>$1</sup>');
    
    // Convert LaTeX subscripts
    processedText = processedText.replace(/_(\d+)/g, '<sub>$1</sub>');
    processedText = processedText.replace(/_\{([^}]+)\}/g, '<sub>$1</sub>');
    
    // Convert LaTeX symbols
    processedText = processedText.replace(/\\alpha/g, 'α');
    processedText = processedText.replace(/\\beta/g, 'β');
    processedText = processedText.replace(/\\gamma/g, 'γ');
    processedText = processedText.replace(/\\delta/g, 'δ');
    processedText = processedText.replace(/\\theta/g, 'θ');
    processedText = processedText.replace(/\\pi/g, 'π');
    processedText = processedText.replace(/\\infty/g, '∞');
    processedText = processedText.replace(/\\leq/g, '≤');
    processedText = processedText.replace(/\\geq/g, '≥');
    processedText = processedText.replace(/\\neq/g, '≠');
    processedText = processedText.replace(/\\approx/g, '≈');
    processedText = processedText.replace(/\\pm/g, '±');
    processedText = processedText.replace(/\\times/g, '×');
    processedText = processedText.replace(/\\div/g, '÷');
    processedText = processedText.replace(/\\sqrt/g, '√');
    processedText = processedText.replace(/\\sum/g, '∑');
    processedText = processedText.replace(/\\int/g, '∫');
    
    // Convert dollar signs to math mode
    processedText = processedText.replace(/\$([^$]+)\$/g, '<span class="math-mode">$1</span>');
    
    return processedText;
  };

  const samples = [
    {
      id: 'quadratic',
      title: 'Quadratic Equations',
      description: 'Solve quadratic equations with complete knowledge graph',
      icon: Calculator,
      color: 'blue',
      samples: allSamples.quadratic
    },
    {
      id: 'circle_geometry',
      title: 'Circle Geometry',
      description: 'Find circle equations and geometric properties',
      icon: Network,
      color: 'green',
      samples: allSamples.circleGeometry
    },
    {
      id: 'factor_remainder',
      title: 'Factor & Remainder Theorem',
      description: 'Find remainders and factors of polynomials',
      icon: BookOpen,
      color: 'purple',
      samples: allSamples.factorRemainder
    },
    {
      id: 'probability',
      title: 'Probability',
      description: 'Calculate probabilities and statistical measures',
      icon: TrendingUp,
      color: 'orange',
      samples: allSamples.probability
    },
    {
      id: 'statistics',
      title: 'Statistics',
      description: 'Calculate mean, median, and standard deviation',
      icon: Brain,
      color: 'indigo',
      samples: allSamples.statistics
    },
    {
      id: 'trigonometry',
      title: 'Trigonometry',
      description: 'Find trigonometric values using special angles',
      icon: Lightbulb,
      color: 'amber',
      samples: allSamples.trigonometry
    },
    {
      id: 'number_system',
      title: 'Number System & Approximation',
      description: 'Work with number systems and approximations',
      icon: Hash,
      color: 'teal',
      samples: allSamples.numberSystem
    },
    {
      id: 'identities',
      title: 'Identities',
      description: 'Prove and apply mathematical identities',
      icon: Square,
      color: 'pink',
      samples: allSamples.identities
    },
    {
      id: 'simultaneous_equations',
      title: 'Simultaneous Linear Equations',
      description: 'Solve systems of linear equations',
      icon: Target,
      color: 'lime',
      samples: allSamples.simultaneousEquations
    },
    {
      id: 'geometry',
      title: 'Angles, Parallel Lines, Triangles & Quadrilaterals',
      description: 'Work with geometric angles and shapes',
      icon: Triangle,
      color: 'cyan',
      samples: allSamples.geometry
    },
    {
      id: 'locus',
      title: 'Locus Problems',
      description: 'Find loci of points with given geometric conditions',
      icon: Target,
      color: 'rose',
      samples: allSamples.locus
    },
    {
      id: 'percentages',
      title: 'Percentages',
      description: 'Calculate percentages and related problems',
      icon: Calculator,
      color: 'emerald',
      samples: allSamples.percentages
    },
    {
      id: 'transformation_symmetry',
      title: 'Transformation & Symmetry',
      description: 'Work with geometric transformations and symmetry',
      icon: Network,
      color: 'violet',
      samples: allSamples.transformationSymmetry
    },
    {
      id: 'exponential_logarithm',
      title: 'Exponential & Logarithmic Functions',
      description: 'Solve exponential and logarithmic equations',
      icon: TrendingUp,
      color: 'orange',
      samples: allSamples.exponentialLogarithm
    },
    {
      id: 'inequalities_linear_programming',
      title: 'Inequalities & Linear Programming',
      description: 'Solve inequalities and linear programming problems',
      icon: Target,
      color: 'red',
      samples: allSamples.inequalitiesLinearProgramming
    },
    {
      id: 'rates_ratios_variations',
      title: 'Rates, Ratios & Variations',
      description: 'Work with rates, ratios and variations',
      icon: BarChart3,
      color: 'indigo',
      samples: allSamples.ratesRatiosVariations
    },
    {
      id: 'arithmetic_geometric_sequences',
      title: 'Arithmetic & Geometric Sequences',
      description: 'Work with arithmetic and geometric sequences',
      icon: Hash,
      color: 'teal',
      samples: allSamples.arithmeticGeometricSequences
    },
    {
      id: 'mensuration',
      title: 'Mensuration',
      description: 'Calculate areas and volumes of geometric shapes',
      icon: Square,
      color: 'blue',
      samples: allSamples.mensuration
    },
    {
      id: 'trigonometry_2d_3d',
      title: 'Trigonometry in 2D & 3D',
      description: 'Apply trigonometry to 2D and 3D problems',
      icon: Compass,
      color: 'amber',
      samples: allSamples.trigonometry2D3D
    },
    {
      id: 'straight_line_equations',
      title: 'Straight Line Equations',
      description: 'Work with equations of straight lines',
      icon: Move,
      color: 'lime',
      samples: allSamples.straightLineEquations
    }
  ];

  const difficulties = [
    { id: 'fundamental', name: 'Fundamental', color: 'green', icon: Star },
    { id: 'intermediate', name: 'Intermediate', color: 'yellow', icon: Star },
    { id: 'harder', name: 'Harder', color: 'red', icon: Star }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      amber: 'bg-amber-50 border-amber-200 text-amber-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      lime: 'bg-lime-50 border-lime-200 text-lime-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      rose: 'bg-rose-50 border-rose-200 text-rose-800'
    };
    return colorMap[color] || 'bg-gray-50 border-gray-200 text-gray-800';
  };

  const getDifficultyColor = (difficulty) => {
    const colorMap = {
      fundamental: 'bg-green-100 text-green-800 border-green-200',
      intermediate: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      harder: 'bg-red-100 text-red-800 border-red-200'
    };
    return colorMap[difficulty] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const renderSampleCard = (sample) => {
    const Icon = sample.icon;
    const colorClasses = getColorClasses(sample.color);
    
    // For regular samples, use difficulty-based structure
    const currentDifficultySamples = sample.samples[selectedDifficulties[sample.id]] || [];
    const sampleCount = currentDifficultySamples.length;
    const firstSample = currentDifficultySamples[0];

    return (
      <div key={sample.id} className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className={`p-2 rounded-lg ${colorClasses}`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{sample.title}</h3>
              <p className="text-sm text-gray-600">{sample.description}</p>
            </div>
          </div>

          {/* Difficulty Selector */}
          <div className="mb-4">
            <div className="flex gap-2">
              {difficulties.map((difficulty) => (
                <button
                  key={difficulty.id}
                  onClick={() => setSelectedDifficulties(prev => ({ ...prev, [sample.id]: difficulty.id }))}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    selectedDifficulties[sample.id] === difficulty.id
                      ? getDifficultyColor(difficulty.id)
                      : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <difficulty.icon className="w-3 h-3" />
                  {difficulty.name}
                </button>
              ))}
            </div>
          </div>

          {/* Sample Problem Preview */}
          {firstSample && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Available Problems:</span>
                <span className="font-medium text-gray-800">{sampleCount}</span>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-700 font-medium mb-2">Sample Problem:</p>
                <div 
                  className="text-sm text-gray-600"
                  dangerouslySetInnerHTML={{ __html: renderLatexText(firstSample.problem) }}
                />
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Knowledge Graph:</span>
                <span className="font-medium text-gray-800">
                  {firstSample.graphData.nodes.length} entities, {firstSample.graphData.links.length} relationships
                </span>
              </div>
            </div>
          )}

          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setSelectedSample(sample)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>
            <button
              onClick={() => {
                if (onSampleSelect && firstSample) {
                  // Pass the first sample
                  onSampleSelect(firstSample);
                }
              }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Play className="w-4 h-4" />
              Interactive
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSamplePreview = () => {
    if (!selectedSample) return null;

    const Icon = selectedSample.icon;
    const colorClasses = getColorClasses(selectedSample.color);
    
    // For regular samples, use difficulty-based structure
    const currentDifficultySamples = selectedSample.samples[selectedDifficulties[selectedSample.id]] || [];
    const sectionTitle = `${selectedDifficulties[selectedSample.id].charAt(0).toUpperCase() + selectedDifficulties[selectedSample.id].slice(1)} Problems`;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${colorClasses}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">{selectedSample.title}</h2>
                  <p className="text-sm text-gray-600">{selectedSample.description}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedSample(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {/* Difficulty Selector */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 mb-3">Select Difficulty Level:</h3>
              <div className="flex gap-3">
                {difficulties.map((difficulty) => (
                  <button
                    key={difficulty.id}
                    onClick={() => setSelectedDifficulties(prev => ({ ...prev, [selectedSample.id]: difficulty.id }))}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                      selectedDifficulties[selectedSample.id] === difficulty.id
                        ? getDifficultyColor(difficulty.id)
                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <difficulty.icon className="w-4 h-4" />
                    {difficulty.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Problems List */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800">
                {sectionTitle} ({currentDifficultySamples.length})
              </h3>
              
              <div className="grid gap-4">
                {currentDifficultySamples.map((problem, index) => (
                  <div key={problem.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-800">Problem {index + 1}</h4>
                                             <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                         getDifficultyColor(selectedDifficulties[selectedSample.id])
                       }`}>
                         {selectedDifficulties[selectedSample.id]}
                       </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">{problem.problem}</p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Entities:</span>
                        <span className="ml-2 font-medium">{problem.graphData.nodes.length}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Relationships:</span>
                        <span className="ml-2 font-medium">{problem.graphData.links.length}</span>
                      </div>
                    </div>
                    
                    {/* Action buttons for each problem */}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => {
                          if (onSampleSelect) {
                            onSampleSelect(problem);
                            setSelectedSample(null);
                          }
                        }}
                        className="flex items-center gap-2 px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 transition-colors"
                      >
                        <Play className="w-3 h-3" />
                        Launch Interactive
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => {
                  if (onSampleSelect && currentDifficultySamples.length > 0) {
                    onSampleSelect(currentDifficultySamples[0]);
                    setSelectedSample(null);
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Play className="w-4 h-4" />
                Launch Interactive View
              </button>
              <button
                onClick={() => {
                  // Download functionality would go here
                  console.log('Download sample data');
                }}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download Data
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Add LaTeX styles */}
      <style>
        {`
          .frac {
            display: inline-block;
            vertical-align: middle;
            text-align: center;
            margin: 0 2px;
          }
          .frac .num {
            display: block;
            border-bottom: 1px solid;
            padding: 0 2px;
            font-size: 0.9em;
          }
          .frac .denom {
            display: block;
            padding: 0 2px;
            font-size: 0.9em;
          }
          .math-mode {
            font-family: 'Computer Modern', serif;
            font-style: italic;
          }
          sup, sub {
            font-size: 0.75em;
            line-height: 1;
          }
        `}
      </style>
      
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Sample Knowledge Graphs</h2>
        <p className="text-gray-600">
          Explore pre-built knowledge graphs for common mathematical topics with different difficulty levels. Each sample demonstrates how mathematical concepts are interconnected.
        </p>
      </div>

      {/* Sample Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {samples.map(renderSampleCard)}
      </div>

      {/* Preview Modal */}
      {selectedSample && renderSamplePreview()}
    </div>
  );
};

export default KnowledgeGraphSamples; 